import React, { useRef, useMemo, useState, useEffect } from "react";
import {
    Input, Form, Icon, Schema,
    Button, Checkbox, Whisper, Tooltip, SelectPicker,
    FormControl
} from "rsuite";
import styled, { css } from "styled-components";

import { checkPermissionsFor } from "store/storeHelpers";
import { hasErrorObject } from "utils";
import {
    SMS_TRAFFIC_LIMITS_REMOVE_API,
    SMS_TRAFFIC_LIMITS_MODIFY_API
} from "const/apiMethods";
import TableEditCellsNew from "components/Table/TableEditCells/TableEditCellsNew";
import Modal from "components/Modal";

const { StringType, NumberType } = Schema.Types;


export default ({
    data,
    loading,
    service,
    editData,
    rowIdKey,
    isSupervisorSms,
    isSupplier,
    isMobile,

    mccList,
    e212List,
    editKeys,
    servicePlanList,
    suppliersGroupList,
    subdestinationList,
    destinationList,
    worldzoneList,

    trafficTypeState,

    setEditData,
    setEditKeys,
    setShowCreateModal,
    setSelectedDefaultData,

    ...props
}) => {

    const defaultFormValue = {};

    const form = useRef(null);

    const [showAddNewModal, setShowAddNewModal] = useState(false);

    const [createFormValue, setCreateFormValue] = useState(defaultFormValue);

    const isGanGroup = trafficTypeState === "gan_group";
    const isMcc = trafficTypeState === "mcc";
    const isGanNumber = trafficTypeState === "gan";
    const isDefault = trafficTypeState === "mcc_default";

    const dayModel = Schema.Model({
        day: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required")
    });

    const hourModel = Schema.Model({
        hour: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required")
    });

    const hourNightModel = Schema.Model({
        hour_night: NumberType()
            .min(1, "Minimum 1")
            .max(10000000, "Maximum 10000000")
            .isRequired("This field is required")
    });

    const limitCombinedMccModel = Schema.Model.combine(
        dayModel, hourModel, hourNightModel
    );


    const limitCombinedGanGroupModel = Schema.Model.combine(
        dayModel, hourModel
    );

    const maxDurationModel = Schema.Model({
        call_duration_max: NumberType().min(1, "Enter a positive number").max(10800, "3 hours is maximum").isRequired("Required")
    });

    const newTrafficLimitModel = Schema.Model({
        sp_key: NumberType().isRequired("This is required field")
    });

    const newSupplierTrafficLimitModel = Schema.Model({
        ...(["gan_group", "gan", "prefix", "mcc"].includes(trafficTypeState)
            ? {
                senderid: StringType()
                    .maxLength(40, "Maximum 40")
                    .isRequired("This is required field"),

            } : {})
    });

    const formModel = Schema.Model.combine(
        maxDurationModel,
        isSupplier ? newSupplierTrafficLimitModel : newTrafficLimitModel
    );

    const applyValueToAll = (key, value) => {
        const filterDefaults = (list) => list.filter(item => !item?.undeleteble);

        let newDraft = [];

        if (key === "remove") {
            if (value) {
                setEditKeys(filterDefaults(editData.draft).map(item => item.key));
            } else {
                setEditKeys([]);
            }
            return;
        }

        setEditData(prev => {
            newDraft = prev.draft.map(item => ({
                ...item,
                [key]: value,
                edited: true
            }));
            return { ...prev, draft: newDraft };
        });
    };

    const updateRows = (rows, key, dataKey, value) =>
        rows.map(row =>
            row.key === key
                ? { ...row, edited: true, [dataKey]: value }
                : row
        );


    const columns = useMemo(() => {
        // displayWhen: ["gan_group", "gan", "prefix", "mcc"],
        if (isSupplier || isDefault) {
            const daytimeName = isGanGroup || isGanNumber
                ? "Hour"
                : <>
                    <span>Daytime hour</span>
                    <Whisper
                        speaker={<Tooltip>From 7:00 to 22:00 hours in local time</Tooltip>}
                        placement="bottomEnd"
                        trigger="hover"
                    >
                        <Icon icon="question-circle" style={{ marginLeft: 5, color: "#ccc" }} />
                    </Whisper>
                </>;

            const nighttimeName = <>
                <span>Nighttime hour</span>
                <Whisper
                    speaker={<Tooltip>From 22:00 to 7:00 hours in local time</Tooltip>}
                    placement="bottomEnd"
                    trigger="hover"
                >
                    <Icon icon="question-circle" style={{ marginLeft: 5, color: "#ccc" }} />
                </Whisper>
            </>;

            return [
                ...(isDefault ? [
                    {
                        id: "trunk_type",
                        name: "Trunk",
                        placeholder: "Trunk...",
                        dataKey: "trunk_type",
                        defaultValue: ({ supplier_trunk }) => {
                            return supplier_trunk?.name || "";
                        },
                        value: ({ supplier_trunk }) => {
                            return `${supplier_trunk?.name || ""}`;
                        },
                        flexGrow: 1,
                        displayWhen: ["mcc_default", "supplier"],
                    },
                    {
                        id: "default_type",
                        name: "Type",
                        placeholder: "Type",
                        dataKey: "defaultType",
                        flexGrow: 1,
                        displayWhen: ["mcc_default", "supplier"],
                    }
                ] : []),
                ...(isGanNumber ? [{
                    id: "gan_number",
                    name: "Number",
                    placeholder: "Number",
                    dataKey: "gan_number",
                    flexGrow: 2,
                    displayWhen: ["gan"],
                    // model: maxDurationModel,
                }] : []),
                ...(isSupplier ? [{
                    id: "group_name",
                    name: "Group name",
                    placeholder: "Group name",
                    dataKey: "group_name",
                    flexGrow: 3,
                    displayWhen: ["gan_group", "gan"],
                    defaultValue: (params) => params["gan_group_id"],
                    value: ({ gan_group_name }) => `${gan_group_name}`
                }] : []),
                ...(isMcc ? [
                    {
                        id: "trunk_type",
                        name: "Trunk",
                        placeholder: "Trunk...",
                        dataKey: "trunk_type",
                        defaultValue: ({ supplier_trunk }) => {
                            return supplier_trunk?.name || "";
                        },
                        value: ({ supplier_trunk }) => {
                            return `${supplier_trunk?.name || ""}`;
                        },
                        flexGrow: 1,
                        displayWhen: ["prefix", "mcc"],
                    },
                    {
                        id: "mcc",
                        name: "Operator",
                        placeholder: "Operator",
                        dataKey: "mcc",
                        defaultValue: ({ mccmnc_name }) => mccmnc_name,
                        value: ({ mccmnc_name }) => `${mccmnc_name}`,
                        flexGrow: 3,
                        displayWhen: ["prefix", "mcc"],
                    }
                ] : []),
                {
                    id: "senderid",
                    name: "Sender ID",
                    placeholder: "Sender ID",
                    dataKey: "senderid",
                    value: ({ senderid }) => senderid || "Any senderId",
                    flexGrow: 3,
                    displayWhen: ["gan_group", "gan", "prefix", "mcc", "supplier", "mcc_default"],
                },
                {
                    id: "day",
                    name: "Day",
                    placeholder: "Day",
                    dataKey: "day",
                    value: ({ key, first, day, count_day }) => {
                        if (editData.mode === "multiple") {
                            const currentValue = editData.draft && editData.draft.find(item => item.key === key);

                            let firstLimitsHaveErrors = false;

                            if (currentValue) {
                                const firstLimitsCheck = first ? limitCombinedGanGroupModel.check(currentValue) : {};
                                firstLimitsHaveErrors = hasErrorObject(firstLimitsCheck);
                            }

                            const applyLimitsToAllDisabled = editData.draft && !editData.draft.length || firstLimitsHaveErrors;

                            return (<TableForm
                                model={dayModel}
                                formValue={currentValue}
                                formDefaultValue={{ day }}
                                onChange={(formValue) => {
                                    // setData
                                    setEditData((prev) => {
                                        return {
                                            ...prev,
                                            draft: updateRows(prev.draft, key, "day", formValue["day"])
                                        };
                                    });
                                }}
                            >
                                <MultiPanel>
                                    <FormControl
                                        name="day"
                                    />
                                    {first && (isGanGroup || isMcc) && <Whisper
                                        speaker={<Tooltip>Apply day to all rows</Tooltip>}
                                        placement="bottomEnd"
                                    >
                                        <StyledButton
                                            color="blue"
                                            disabled={applyLimitsToAllDisabled}
                                            onClick={() => {
                                                const currentHour = currentValue && currentValue.day ? currentValue.day : day;

                                                applyValueToAll("day", currentHour);
                                            }}
                                        >
                                            <Icon icon="arrow-down" />
                                        </StyledButton>
                                    </Whisper>}
                                </MultiPanel>
                            </TableForm>)
                        }

                        return isDefault && count_day === undefined ? `${day}` : `${day}/${count_day}`;
                    },
                    displayWhen: ["gan_group", "gan", "prefix", "mcc", "supplier", "mcc_default"],
                    width: 190,
                    accepter: Input,
                    model: dayModel,
                },
                {
                    id: "hour",
                    name: daytimeName,
                    placeholder: "Daytime hour",
                    dataKey: "hour",
                    value: ({ key, first, day, hour, count_hour }) => {
                        if (editData.mode === "multiple") {
                            const currentValue = editData.draft && editData.draft.find(item => item.key === key);
                            let firstLimitsHaveErrors = false;

                            if (currentValue) {
                                const firstLimitsCheck = first ? limitCombinedGanGroupModel.check(currentValue) : {};
                                firstLimitsHaveErrors = hasErrorObject(firstLimitsCheck);
                            }

                            const applyLimitsToAllDisabled = editData.draft && !editData.draft.length || firstLimitsHaveErrors;

                            return (<TableForm
                                model={hourModel}
                                formValue={currentValue}
                                formDefaultValue={{ hour }}
                                onChange={(formValue) => {
                                    // setData
                                    setEditData((prev) => {
                                        return {
                                            ...prev,
                                            draft: updateRows(prev.draft, key, "hour", formValue["hour"])
                                        }
                                    })
                                }}
                            >
                                <MultiPanel>
                                    <FormControl
                                        name="hour"
                                    />

                                    {first && (isGanGroup || isMcc) && <Whisper
                                        speaker={<Tooltip>Apply hour to all rows</Tooltip>}
                                        placement="bottomEnd"
                                    >
                                        <StyledButton
                                            color="blue"
                                            disabled={applyLimitsToAllDisabled}
                                            onClick={() => {
                                                const currentHour = currentValue && currentValue.hour ? currentValue.hour : hour;

                                                applyValueToAll("hour", currentHour);
                                            }}
                                        >
                                            <Icon icon="arrow-down" />
                                        </StyledButton>
                                    </Whisper>}
                                </MultiPanel>
                            </TableForm>)
                        }

                        return isDefault && count_hour === undefined ? `${hour}` : `${hour}/${count_hour}`;
                    },
                    displayWhen: ["gan_group", "gan", "prefix", "mcc", "supplier", "mcc_default"],
                    width: 200,
                    accepter: Input,
                    model: hourModel,
                },
                {
                    id: "hour_night",
                    name: nighttimeName,
                    placeholder: "Nighttime hour",
                    dataKey: "hour_night",
                    value: ({ key, first, day, hour, hour_night, count_hour, type }) => {
                        if (editData.mode === "multiple") {
                            const currentValue = editData.draft && editData.draft.find(item => item.key === key);

                            let firstLimitsHaveErrors = false;

                            if (currentValue) {
                                const firstLimitsCheck = first ? limitCombinedMccModel.check(currentValue) : {};
                                firstLimitsHaveErrors = hasErrorObject(firstLimitsCheck);
                            }

                            const applyLimitsToAlDisabled = editData.draft && !editData.draft.length || firstLimitsHaveErrors;

                            return (<TableForm
                                model={hourNightModel}
                                formValue={currentValue}
                                formDefaultValue={{ hour_night }}
                                onChange={(formValue) => {
                                    // setData
                                    setEditData((prev) => {
                                        return {
                                            ...prev,
                                            draft: updateRows(prev.draft, key, "hour_night", formValue["hour_night"])
                                        }
                                    })
                                }}
                            >
                                <MultiPanel>
                                    <FormControl
                                        name="hour_night"
                                    />
                                    {first && isMcc && <Whisper
                                        speaker={<Tooltip>Apply nighttime hour to all rows</Tooltip>}
                                        placement="bottomEnd"
                                    >
                                        <StyledButton
                                            color="blue"
                                            disabled={applyLimitsToAlDisabled}
                                            onClick={() => {
                                                const currentHourNight = currentValue && currentValue.hour_night ? currentValue.hour_night : hour_night;

                                                applyValueToAll("hour_night", currentHourNight);
                                            }}
                                        >
                                            <Icon icon="arrow-down" />
                                        </StyledButton>
                                    </Whisper>}
                                </MultiPanel>
                            </TableForm>)
                        }

                        return isDefault && type === "supplier"
                            ? count_hour === undefined
                                ? `${hour}`
                                : `${hour}/${count_hour}`
                            : count_hour === undefined
                                ? `${hour_night}`
                                : `${hour_night || 0}/${count_hour}`;
                    },
                    displayWhen: ["mcc", "supplier", "mcc_default"],
                    // flexGrow: 1,
                    width: 200,
                    disabledOnMissingParam: { "hour_night": "supplier_trunk" },
                    accepter: Input,
                    model: hourModel,
                },
                ...(editData.mode === "multiple" ? [
                    {
                        id: "select",
                        name: "Select to delete",
                        dataKey: "select",
                        // customField: true,
                        // accepter: Checkbox,
                        align: "center",
                        width: 120,
                        value: ({ key, first, undeleteble }) => {
                            const currentValue = editData.draft && editData.draft.find(item => item.key === key);

                            let firstLimitsHaveErrors = false;

                            if (currentValue) {
                                const firstLimitsCheck = first ? limitCombinedGanGroupModel.check(currentValue) : {};
                                firstLimitsHaveErrors = hasErrorObject(firstLimitsCheck);
                            }

                            const applyLimitsToAllDisabled = editData.draft && !editData.draft.length || firstLimitsHaveErrors;

                            return (
                                <MultiPanel noForm>
                                    <StyledCheckbox
                                        disabled={undeleteble}
                                        checked={editKeys.includes(key)}
                                        onChange={(_, checked) => {
                                            if (!editKeys.includes(key) && checked) {
                                                setEditKeys(prev => [...prev, key]);
                                            } else if (editKeys.includes(key) && !checked) {
                                                setEditKeys(prev => prev.filter(k => k !== key));
                                            }
                                        }}
                                    />
                                    {first && (isMcc || isGanGroup) && <Whisper
                                        speaker={<Tooltip>Apply select to all rows</Tooltip>}
                                        placement="bottomEnd"
                                    >
                                        <StyledButton
                                            color="blue"
                                            disabled={applyLimitsToAllDisabled}
                                            onClick={() => {
                                                applyValueToAll("remove", editKeys.includes(key));
                                            }}
                                        >
                                            <Icon icon="arrow-down" />
                                        </StyledButton>
                                    </Whisper>}
                                </MultiPanel>
                            )
                        }
                    }
                ] : [])
            ];
        }

        return [
            {
                id: "sp_key",
                name: "Price",
                dataKey: "sp_key",
                valueKey: "sp_key",
                labelKey: "name",
                placeholder: "Price",

                minWidth: 300,
                flexGrow: 1,

                accepter: SelectPicker,
                cleanable: false,
                data: servicePlanList,

                model: newTrafficLimitModel,

                defaultValue: ({ sp_key }) => sp_key,
                value: ({ service_plan_name }) => service_plan_name,
            },
            {
                id: "wz_key",
                name: "Worldzone",
                dataKey: "wz_key",
                valueKey: "wz_key",
                labelKey: "name",
                placeholder: "Worldzone",

                minWidth: 300,
                flexGrow: 1,

                accepter: SelectPicker,
                data: worldzoneList,

                defaultValue: ({ wz_key }) => wz_key,
                value: ({ worldzone_name }) => worldzone_name,
            },
            {
                id: "de_key",
                name: "Destination",
                dataKey: "de_key",
                valueKey: "de_key",
                labelKey: "name",
                placeholder: "Destination",

                minWidth: 300,
                flexGrow: 1,

                accepter: SelectPicker,
                data: destinationList,

                defaultValue: ({ de_key }) => de_key,
                value: ({ destination_name }) => destination_name,
            },
            ...(service ? [{
                id: "sde_key",
                name: "Subdestination",
                dataKey: "sde_key",
                valueKey: "sde_key",
                labelKey: "name",
                placeholder: "Subdestination",

                minWidth: 300,
                flexGrow: 1,

                accepter: SelectPicker,
                data: subdestinationList,

                defaultValue: ({ sde_key }) => sde_key,
                value: ({ subdestination_name }) => subdestination_name,
            }] : [{
                id: "mcc",
                name: "Operator",
                dataKey: "operator",
                valueKey: "operator",
                labelKey: "name",
                placeholder: "Operator",

                // minWidth: 300,
                flexGrow: 1,

                accepter: SelectPicker,
                data: e212List,

                defaultValue: ({ mcc, mnc }) => `${mcc}${mnc}`,
                value: ({ country_name, operator, mcc, mnc }) => `${country_name}, ${operator} (${mcc}${mnc})`,
            }]),
            {
                id: "call_duration_max",
                name: "Max Duration",
                dataKey: "call_duration_max",
                placeholder: "Max Duration",

                // minWidth: 200,
                width: 130,
                // flexGrow: 1,

                accepter: Input,
                model: maxDurationModel
            },
        ];
    }, [service, editData, isSupplier, editKeys, trafficTypeState, mccList, servicePlanList, worldzoneList, destinationList, subdestinationList]);

    const convertedData = useMemo(() => {
        return data.map((value, idx) => {
            if (idx === 0) {
                value.first = true;
            }
            if ("gan_key" in value) {
                value.senderid_gan_key = `${value?.senderid}_${value?.gan_key}`;
                value.key = value.senderid_gan_key;
            }

            if (value?.undeleteble) {
                value.onRowSubmitDefault = () => {
                    const selectedData = {
                        ...(value.supplier_trunk && value.supplier_trunk.id ? { "trunk_id": value.supplier_trunk.id } : {}),
                        mcc: value.mcc,
                        mnc: value.mnc,
                        hour: value.hour,
                        hour_night: value.hour_night,
                        day: value.day,
                        senderid: value.senderid
                    };
                    setSelectedDefaultData(selectedData);
                    setShowCreateModal(true);
                };
            }
            return value;
        });
    }, [data, trafficTypeState]);

    const filteredColumns = columns.filter(el => "displayWhen" in el ? el.displayWhen.indexOf(trafficTypeState) !== -1 : true);

    const showModifyButton = !isSupervisorSms || isSupervisorSms && checkPermissionsFor(SMS_TRAFFIC_LIMITS_MODIFY_API);
    const showRemoveButton = !isSupervisorSms || isSupervisorSms && checkPermissionsFor(SMS_TRAFFIC_LIMITS_REMOVE_API);

    const addNewButton = (
        <AddNewButton
            className="tableFilters_submit"
            color="green"
            onClick={() => {
                setShowAddNewModal(true);
            }}
        >
            Add new
        </AddNewButton>
    );

    const handleSubmit = (_, event) => {
        event.preventDefault();

        let validStatus = form.current.check();

        if (!validStatus)
            return;

        let formValues = form.current.getFormValue();
        let data = {
            ...formValues,
            call_duration_max: +formValues.call_duration_max
        };

        props.onRowCreate(data);

        props.onDataUpdate();

        if (isMobile && showAddNewModal) {
            setShowAddNewModal(false);
        }
    };

    const handleChange = (value) => {
        setCreateFormValue(value);
    };

    // onDataUpdate={onDataUpdate}
    // onRowCreate={handleCreateTrafficLimit}

    const defaultCreateFormValue = useMemo(() => service ? {
        sp_key: null,
        wz_key: null,
        de_key: null,
        sde_key: null,
        call_duration_max: ""
    } : {}, [service]);

    return (
        <>
            <StyledTable
                rowIdKey={rowIdKey}
                defaultRowIdKey="senderid_gan_key"
                rowClassName={(rowData) => {
                    if (rowData)
                        return rowData.undeleteble ? "undeletable-row" : ""
                }}
                virtualized
                loading={loading}
                data={convertedData}
                height={500}
                columns={filteredColumns}
                headerHeight={40}
                autoHeight={false}
                shouldUpdateScroll={false}
                formModel={formModel}
                addButtonWidth={editData.mode !== "multiple" ? 135 : 1}
                canCreate={!!service && !isMobile}
                canModify={!isGanNumber && showModifyButton && editData.mode !== "multiple"}
                canRemove={!isGanNumber && showRemoveButton && editData.mode !== "multiple"}
                defaultDisableKey="trunk_lm_key"
                {...props}
                createFormDefaultValue={defaultCreateFormValue}
            />
            {isMobile && !!service && <>
                <>{addNewButton}</>
                {showAddNewModal && (
                    <Modal
                        show={showAddNewModal}
                        onClose={() => {
                            setShowAddNewModal(false);
                        }}
                    >
                        <StyledForm
                            ref={form}
                            model={formModel}
                            onSubmit={handleSubmit}
                            onChange={handleChange}
                            formDefaultValue={createFormValue}
                        // style={{margin: "0px", width: "100%"}}
                        >
                            <FormControl
                                errorPlacement="topEnd"
                                className="tableFilters_field"
                                name="sp_key"
                                accepter={SelectPicker}
                                data={servicePlanList}
                                valueKey="sp_key"
                                labelKey="name"
                                placeholder="Price"
                            />
                            <FormControl
                                errorPlacement="topEnd"
                                className="tableFilters_field"
                                name="wz_key"
                                accepter={SelectPicker}
                                data={worldzoneList}
                                valueKey="wz_key"
                                labelKey="name"
                                placeholder="Worldzone"
                            />
                            <FormControl
                                errorPlacement="topEnd"
                                className="tableFilters_field"
                                name="de_key"
                                accepter={SelectPicker}
                                data={destinationList}
                                valueKey="de_key"
                                labelKey="name"
                                placeholder="Destination"
                            />
                            <FormControl
                                errorPlacement="topEnd"
                                className="tableFilters_field"
                                name="sde_key"
                                accepter={SelectPicker}
                                data={subdestinationList}
                                valueKey="sde_key"
                                labelKey="name"
                                placeholder="Subdestination"
                            />
                            <FormControl
                                errorPlacement="topEnd"
                                className="tableFilters_field"
                                name="call_duration_max"
                                accepter={Input}
                                placeholder="Max Duration"
                            />
                            <Button
                                type="submit"
                                color="green"
                            >
                                Submit
                            </Button>
                        </StyledForm>
                    </Modal>
                )}
            </>}
        </>
    );
};

const StyledTable = styled(TableEditCellsNew)`
    &&.rs-table.rs-table-hover {
        margin-top: 0;
        margin-bottom: 0;
    }

    .rs-table-row.undeletable-row .rs-table-cell-content {
        background: lightgray;
    }

    .rs-table-body-row-wrapper .rs-table-cell-content .tableFilters_buttons {
        float: right;
    }
    .rs-table-body-row-wrapper .rs-table-cell-content .tableFilters_buttons > .rs-btn {
        width: 28px;
        height: 28px;
        padding: 4px 8px;
    }
    
    .rs-table-body-row-wrapper .rs-table-cell-content .tableFilters_buttons > .rs-btn {
        margin-right: 5px;
    }
    .rs-table-body-row-wrapper .rs-table-cell-content .tableFilters_buttons .rs-btn-lightblue {
        color: var(--color-light);
        background: var(--color-info);
    }
    
    .rs-error-message.rs-error-message-show {
    
        position: absolute;
        color: red;
        top: -4px;
        font-size: 10px;
        border-radius: 10px;
        bottom: auto !important;
        padding: 0 4px;
        right: 6px !important;
    }
    
    .rs-error-message.rs-error-message-show .rs-error-message-arrow {
        display: none;
    }
    .rs-error-message.rs-error-message-show .rs-error-message-inner {
        white-space: nowrap;
    }
`;

const AddNewButton = styled(Button)`
    && {
        margin-top: 20px;
        width: 100%;
    }
`;

const StyledForm = styled(Form)`
    && {
        .rs-form-control-wrapper {
            margin-bottom: 15px;
        }
        .rs-btn {
            width: 100%;
        }
    }
`;

const TableForm = styled(Form)`
    && {
        margin-top: -7px;
    }

    && .rs-input {
        overflow: visible !important;
    }

    && .rs-error-message.rs-error-message-show {
        margin-top: -12px;
        border-radius: 5px;
        padding: 0px 6px;
        line-height: 14px;
        right: inherit !important;
        left: 0;
    }
`;

const StyledButton = styled(Button)`
    && {
        /* margin-top: -4px; */
        margin-left: 5px;
        min-width: 22px;
        height: 36px;
        padding: 0px;
    }
`;

const MultiPanel = styled.div`
    && {
        display: flex;
        flex-direction: row;
        justify-content: end;
        align-items: center;
        margin-top: -4px;
    }

    ${props => props.noForm && css`
        && {
            margin-top: -7px;
        }
    `}
`;

const StyledCheckbox = styled(Checkbox)`
    && {
        margin-left: 42px;
        /* margin-top: -4px; */
        width: 36px;
    }
`;